/**
 * The two case file viewers (docs/ux/casefile.md, D-0081). "View Unsorted
 * Case Files" shows the bundle as received, one PDF in the browser's own
 * viewer. "View Sorted Case Files" shows the working casefile: the machine
 * result with every saved change applied, as the bookmark tree beside the
 * chosen document's pages, so the sorted result adjusts as decisions are
 * saved; the reviewed Sorted.pdf itself is built when the review is
 * completed. Both are modal, asked for in the first real review. The PDFs
 * render inside iframes, so no bundle text enters the tab's own DOM.
 */

import { useEffect, useMemo, useState } from 'react';
import type { CasefileManifest, SortRunHead, SortRunView, SortedTreeDocument } from '@/app/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { pagesText } from './describe';
import { OpenPdfLink } from './ReplayPanel';
import { documentPdfUrl, sortedPdfUrl, sourcePdfUrl } from './useSort';

export type ViewerMode = 'unsorted' | 'sorted';

/** A document whose section, pages or title differ from the machine's, or which the machine never had (a split). */
function isChanged(doc: SortedTreeDocument, section: string, machine: CasefileManifest): boolean {
  const m = machine.documents.find((d) => d.id === doc.id);
  if (!m) return true;
  return m.section !== section || m.title !== doc.title || m.startPage !== doc.startPage || m.endPage !== doc.endPage || (m.duplicateOf ?? null) !== doc.duplicateOf;
}

function PdfFrame({ src, title }: { src: string; title: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <iframe src={src} title={title} className="min-h-0 w-full flex-1 border-0 bg-beam-cream-deep" />
      <p className="px-4 py-2 text-xs text-beam-text-muted">
        If the file does not show here, <OpenPdfLink href={src} className="min-h-0 text-xs font-normal">open it in a new tab</OpenPdfLink>.
      </p>
    </div>
  );
}

export function ViewerDialog({ matterRef, head, view, mode, locked, onClose, onChangeDocument }: {
  matterRef: string;
  head: SortRunHead;
  view: SortRunView;
  mode: ViewerMode | null;
  locked: boolean;
  onClose: () => void;
  onChangeDocument: (documentId: string) => void;
}) {
  const runId = head.runId;
  const tree = view.working.tree;
  const docs = useMemo(() => tree.flatMap((sec) => sec.groups.flatMap((g) => g.documents.map((d) => ({ ...d, section: sec.section })))), [tree]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [whole, setWhole] = useState(false);
  useEffect(() => {
    if (mode !== 'sorted') return;
    setWhole(false);
    setSelectedId((id) => (id && docs.some((d) => d.id === id) ? id : (docs.find((d) => !d.duplicateOf) ?? docs[0])?.id ?? null));
  }, [mode, docs]);
  const selected = docs.find((d) => d.id === selectedId) ?? null;
  const applied = view.working.applied;
  const built = head.reviewedPointer !== null;
  const totalPages = head.machine.totalPages;

  return (
    <Dialog open={mode !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-slot="viewer-dialog" className="flex h-[92svh] w-[min(1280px,96vw)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
        <DialogHeader className="border-b border-beam-line px-4 py-3 text-left">
          <DialogTitle>{mode === 'unsorted' ? 'Unsorted case files' : 'Sorted case files'}</DialogTitle>
          <DialogDescription>
            {mode === 'unsorted'
              ? `${totalPages} pages as received, in the order they arrived.`
              : applied > 0
                ? `As reviewed so far: ${applied} ${applied === 1 ? 'change' : 'changes'} applied to the machine result, which is kept as it is. The reviewed Sorted.pdf is built when the review is completed.`
                : 'The machine result. Changes you save appear here as you go; the reviewed Sorted.pdf is built when the review is completed.'}
          </DialogDescription>
        </DialogHeader>
        {mode === 'unsorted' ? <PdfFrame src={sourcePdfUrl(matterRef, runId)} title={`Unsorted case files, ${totalPages} pages as received`} /> : null}
        {mode === 'sorted' ? (
          <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,40%)_minmax(0,1fr)] md:grid-cols-[320px_minmax(0,1fr)] md:grid-rows-1">
            <nav aria-label="Sorted documents" className="min-h-0 overflow-y-auto border-b border-beam-line p-3 md:border-r md:border-b-0">
              {view.working.problems.length > 0 ? (
                <Alert className="mb-3">
                  <AlertTitle>Some changes could not be applied</AlertTitle>
                  <AlertDescription><ul className="list-disc pl-4">{view.working.problems.map((p) => <li key={p}>{p}</li>)}</ul></AlertDescription>
                </Alert>
              ) : null}
              <button type="button" aria-pressed={whole} onClick={() => setWhole(true)} className={cn('mb-2 flex min-h-9 w-full items-center rounded-lg px-2 text-left text-[13px] hover:bg-beam-cream-deep', whole && 'bg-beam-cream-deep font-medium')}>
                Whole file{built ? ' (reviewed Sorted.pdf)' : applied > 0 ? ' (machine Sorted.pdf, before your changes)' : ' (machine Sorted.pdf)'}
              </button>
              {tree.map((sec) => (
                <section key={sec.section} className="mb-3">
                  <h3 className="eyebrow px-2 py-1 text-beam-text-muted">{sec.section}</h3>
                  {sec.groups.map((group, gi) => (
                    <div key={`${sec.section}-${group.heading ?? gi}`}>
                      {group.heading ? <p className="px-2 pt-1 text-xs text-beam-text-muted">{group.heading}</p> : null}
                      {group.documents.map((doc) => {
                        const changed = isChanged(doc, sec.section, view.machine.manifest);
                        const active = !whole && doc.id === selectedId;
                        return (
                          <button key={doc.id} type="button" aria-pressed={active} onClick={() => { setWhole(false); setSelectedId(doc.id); }} className={cn('flex min-h-9 w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] hover:bg-beam-cream-deep', active && 'bg-beam-cream-deep font-medium')}>
                            <span className="min-w-0 flex-1 truncate">{doc.title}</span>
                            {changed ? <span className="shrink-0 rounded-full bg-beam-warning-bg px-1.5 text-[11px] text-beam-warning">Changed</span> : null}
                            <span className="tabular shrink-0 text-beam-text-muted">{pagesText(doc.startPage, doc.endPage).replace(/^Pages? /, 'p. ')}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </section>
              ))}
            </nav>
            <div className="flex min-h-0 flex-col">
              {whole ? (
                <PdfFrame src={sortedPdfUrl(matterRef, runId, built ? 'reviewed' : 'machine')} title={built ? 'Reviewed Sorted.pdf' : 'Machine Sorted.pdf'} />
              ) : selected ? (
                <>
                  <div className="flex flex-wrap items-center gap-3 border-b border-beam-line px-4 py-2 text-[13px]">
                    <span className="min-w-0 flex-1 truncate font-medium">{selected.title}</span>
                    <span className="tabular text-beam-text-muted">{pagesText(selected.startPage, selected.endPage)} of the bundle</span>
                    {!locked && !selected.duplicateOf ? <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onChangeDocument(selected.id)}>Change this document</Button> : null}
                  </div>
                  <PdfFrame src={documentPdfUrl(matterRef, runId, selected.startPage, selected.endPage)} title={`${selected.title}, ${pagesText(selected.startPage, selected.endPage).toLowerCase()}`} />
                </>
              ) : <p className="p-4 text-[13px] text-beam-text-muted">No documents.</p>}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
